import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  getDoc,
  query,
  where,
  orderBy,
  startAfter,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";

const doctorsMasterDataCollection = collection(db, "doctors_master_data");

const doctorMasterDataService = {
  getAll: async (limitCount = 200, lastVisible = null) => {
    try {
      let q = query(
        doctorsMasterDataCollection,
        orderBy("first_name"),
        limit(limitCount)
      );

      if (lastVisible) {
        q = query(
          doctorsMasterDataCollection,
          orderBy("first_name"),
          startAfter(lastVisible),
          limit(limitCount)
        );
      }

      const doctorsMasterDataSnapshot = await getDocs(q);
      const doctorsMasterDataList = doctorsMasterDataSnapshot.docs.map(
        (doc) => ({
          id: doc.id,
          ...doc.data(),
        })
      );

      // Get the last visible document for the next page
      const lastDoc =
        doctorsMasterDataSnapshot.docs[
          doctorsMasterDataSnapshot.docs.length - 1
        ];

      return {
        data: doctorsMasterDataList,
        lastVisible: lastDoc,
        // Check if there are more documents to load
        hasMore: doctorsMasterDataSnapshot.docs.length === limitCount,
      };
    } catch (error) {
      console.error("Error fetching doctors Master Data:", error);
      throw error;
    }
  },
  getAllWithoutPagination: async () => {
    try {
      const doctorsMasterDataSnapshot = await getDocs(
        doctorsMasterDataCollection
      );
      const doctorsMasterDataList = doctorsMasterDataSnapshot.docs.map(
        (doc) => ({
          id: doc.id,
          ...doc.data(),
        })
      );
      return doctorsMasterDataList;
    } catch (error) {
      console.error("Error fetching doctors Master Data:", error);
      throw error;
    }
  },
  findDoctor: async (searchField, searchValue) => {
    try {
      const q = query(
        doctorsMasterDataCollection,
        where(searchField, "==", searchValue),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // console.log(`❌ No doctor found with ${searchField}=${searchValue}`);
        return null;
      }

      const match = snapshot.docs[0];
      // console.log(`✅ Found doctor with ${searchField}=${searchValue}`);

      return {
        id: match.id,
        ...match.data(),
      };
    } catch (error) {
      console.error("Error searching doctor in master data:", error);

      // Handle specific Firestore errors
      if (error.code === "failed-precondition") {
        throw new Error(
          `Search field '${searchField}' requires composite index. Check Firebase console.`
        );
      }

      throw error;
    }
  },
  getHCP: async (searchTerm, limitCount = 5000) => {
    try {
      let lastVisible = null;
      let hasMore = true;
      let batchNumber = 1;
      let totalChecked = 0;
      const matchingHCPs = [];
      const searchableFields = [
        "first_name",
        "family_name",
        "email",
        "phone_number",
        "specialty",
        "industry_area",
        "medical_license",
        "organization",
        "national_id",
        "APO_number",
      ];
      const lowerSearch = searchTerm.toLowerCase();

      // Loop through all batches until data ends
      while (hasMore) {
        // Create Firestore query for next batch
        let q = query(
          doctorsMasterDataCollection,
          orderBy("first_name"),
          limit(limitCount)
        );

        if (lastVisible) {
          q = query(
            doctorsMasterDataCollection,
            orderBy("first_name"),
            startAfter(lastVisible),
            limit(limitCount)
          );
        }

        // Fetch batch from Firestore
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          break;
        }

        // Map data
        const batchData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        totalChecked += batchData.length;

        // Search for matches in this batch
        const matchedInBatch = batchData.filter((hcp) =>
          searchableFields.some((field) => {
            const value = hcp[field];
            const match =
              value && String(value).toLowerCase().includes(lowerSearch);

            return match;
          })
        );

        if (matchedInBatch.length > 0) {
          matchingHCPs.push(...matchedInBatch);
        }
        // Update pagination info
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        hasMore = snapshot.docs.length === limitCount;
        lastVisible = lastDoc;
        batchNumber++;
      }

      if (matchingHCPs.length > 0) {
        return matchingHCPs;
      } else {
        return [];
      }
    } catch (error) {
      console.error("💥 Error searching for HCP:", error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const doctorRef = doc(db, "doctors_master_data", id);
      const doctorDoc = await getDoc(doctorRef);
      if (doctorDoc.exists()) {
        return {
          id: doctorDoc.id,
          ...doctorDoc.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching doctor master data by ID:", error);
      throw error;
    }
  },
  add: async (doctorsMasterDataData) => {
    try {
      const docRef = await addDoc(
        doctorsMasterDataCollection,
        doctorsMasterDataData
      );
      return docRef;
    } catch (error) {
      console.error("Error adding doctor master data:", error);
      throw error;
    }
  },
  edit: async ({ id, data }) => {
    try {
      const doctorsMasterDataRef = doc(db, "doctors_master_data", id);
      const snapshot = await getDoc(doctorsMasterDataRef);
      if (!snapshot.exists()) {
        throw new Error("Doctor not found");
      }
      await updateDoc(doctorsMasterDataRef, data);
      return { id, ...data };
    } catch (error) {
      console.error("Error updating doctor master data:", error);
      throw error;
    }
  },
  delete: async (doctorsMasterDataId) => {
    try {
      await deleteDoc(doc(db, "doctors_master_data", doctorsMasterDataId));
      return true;
    } catch (error) {
      console.error("Error deleting doctor master data:", error);
      throw error;
    }
  },
  createFromUserData: async (userData) => {
    try {
      const doctorData = {
        first_name: userData.first_name,
        family_name: userData.family_name,
        email: userData.email_address,
        phone_number: userData.phone_number,
        specialty: userData.specialty,
        country_id: userData.country_id,
        city_id: userData.city_id,
        medical_license: userData.medical_license,
        organization: userData.organization,
        national_id: String(userData.UPI_num),
        APO_number: String(userData.UPI_num), // Set APO_number to national_id value
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const docRef = await addDoc(doctorsMasterDataCollection, doctorData);
      return docRef;
    } catch (error) {
      console.error("Error creating doctor master data from user:", error);
      throw error;
    }
  },
  getByEmail: async (email) => {
    try {
      const q = query(
        doctorsMasterDataCollection,
        where("email", "==", email.toLowerCase())
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching doctor master data by email:", error);
      throw error;
    }
  },
  updateAPONumberForExistingRecords: async () => {
    try {
      const doctorsMasterDataSnapshot = await getDocs(
        doctorsMasterDataCollection
      );
      const updatePromises = [];

      doctorsMasterDataSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        if (!data.APO_number && data.national_id) {
          const updatePromise = updateDoc(docSnapshot.ref, {
            APO_number: data.national_id,
            updated_at: new Date().toISOString(),
          });
          updatePromises.push(updatePromise);
        }
      });

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        console.log(`Updated ${updatePromises.length} records with APO_number`);
      }

      return updatePromises.length;
    } catch (error) {
      console.error("Error updating APO_number for existing records:", error);
      throw error;
    }
  },
  getHCPByNationalId: async (nationalId) => {
    try {
      const q = query(
        doctorsMasterDataCollection,
        where("national_id", "==", nationalId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching doctor master data by national_id:", error);
      throw error;
    }
  },
};

export default doctorMasterDataService;